import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Sparkles,
  Phone,
  Check,
  Info,
  Star,
  Gift,
} from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { getStageById, etatStage } from "../lib/stages";
import { thumb, fallbackToFull } from "../lib/imageThumb";

export default function StageDetail() {
  const { stageId } = useParams();
  const stage = getStageById(stageId);
  // Terminé dès la date de fin passée, « date à venir » tant qu'elle n'est pas fixée (Jen, 30.10.2026).
  const etat = stage ? etatStage(stage) : "";
  const termine = etat === "termine";
  const sansDate = etat === "date_a_venir";

  useSEO({
    title: stage ? `${stage.title} — 1000 ARTS Cugy VD` : "Stage — 1000 ARTS",
    description: stage
      ? `${stage.title} · ${stage.date_label} · ${stage.theme} · Tarifs dès ${stage.tarif_from}. Inscription et infos utiles.`
      : "Stages 1000 ARTS",
    canonical: `/stages/${stageId}`,
    // Un stage dont les inscriptions sont fermées ne doit pas rester dans l'index.
    robots: stage && termine ? "noindex, follow" : undefined,
  });

  if (!stage) {
    return <Navigate to="/stages" replace />;
  }

  const { Icon } = stage;

  return (
    <div
      data-testid={`page-stage-detail-${stage.id}`}
      className="pt-32 pb-24 overflow-hidden relative mesh-bg"
    >
      <PageDecor />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 z-10">
        {/* Retour */}
        <Link
          to="/stages"
          className="inline-flex items-center gap-2 text-sm font-medium mb-6 hover:opacity-70 transition-opacity"
          style={{ color: "var(--muted)" }}
          data-testid="stage-back-link"
        >
          <ArrowLeft size={16} /> Retour aux stages
        </Link>

        {/* Hero */}
        <div className="flex flex-wrap items-start gap-4 mb-3">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-md flex-shrink-0"
            style={{ background: stage.accent, color: "#fff" }}
          >
            <Icon size={26} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow" style={{ color: stage.accent }}>
              {stage.season}
            </div>
            <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight mt-1">
              {stage.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span className="inline-flex items-center gap-2 text-sm text-[var(--muted)]">
                <Calendar size={14} /> {stage.date_label}
              </span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                style={{ background: stage.accent_soft, color: stage.accent }}
              >
                {stage.theme}
              </span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
              >
                {stage.age_range}
              </span>
              {termine ? (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                  style={{ background: "#E5E7EB", color: "#4B5563" }}
                  data-testid="stage-closed-badge"
                >
                  Terminé
                </span>
              ) : sansDate ? (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                  style={{ background: stage.accent_soft, color: stage.accent }}
                  data-testid="stage-comingsoon-badge"
                >
                  Date à venir
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-sm"
                  style={{ background: "var(--primary)", color: "#fff" }}
                  data-testid="stage-open-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Inscriptions ouvertes
                </span>
              )}
            </div>
          </div>
        </div>

        {stage.description && (
          <p
            className="mt-6 text-lg text-[var(--text)] leading-relaxed max-w-3xl"
            dangerouslySetInnerHTML={{ __html: stage.description }}
          />
        )}

        {/* GRID FLYER + INFOS */}
        <div className={`mt-12 grid gap-8 lg:gap-12 ${stage.flyer ? "lg:grid-cols-[1fr_1.05fr]" : "lg:grid-cols-1 max-w-3xl"}`}>
          {/* Flyer (masqué si non fourni) */}
          {stage.flyer && (
            <div
              className="rounded-3xl overflow-hidden self-start"
              style={{
                border: "1px solid var(--border-soft)",
                boxShadow: "0 20px 60px rgba(15,27,45,0.10)",
              }}
            >
              <img
                src={thumb(stage.flyer)} onError={fallbackToFull(stage.flyer)}
                alt={`Flyer — ${stage.title}`}
                className="w-full h-auto block"
                data-testid={`stage-detail-flyer-${stage.id}`}
                loading="eager"
              />
            </div>
          )}

          {/* Infos */}
          <div className="space-y-6">
            {/* Programme */}
            <div className="card-flat">
              <div className="flex items-center gap-2 mb-4">
                <Info size={20} style={{ color: stage.accent }} />
                <h2 className="font-display text-2xl">Infos pratiques</h2>
              </div>
              <ul className="space-y-3">
                {stage.infos.map((row) => (
                  <li
                    key={row.label}
                    className="flex items-start gap-3 pb-3"
                    style={{ borderBottom: "1px dashed var(--border-soft)" }}
                  >
                    <Check
                      size={16}
                      className="mt-1 flex-shrink-0"
                      style={{ color: stage.accent }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--muted)]">
                        {row.label}
                      </div>
                      <div className="text-sm md:text-base mt-0.5">
                        {row.value}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Tarifs (masqués si non renseignés) */}
            {stage.tarifs && stage.tarifs.length > 0 && (
              <div
                className="card-flat"
                style={{ background: stage.accent_soft, borderColor: "transparent" }}
              >
              <div className="flex items-center gap-2 mb-4">
                <Star
                  size={20}
                  style={{ color: stage.accent, fill: stage.accent }}
                />
                <h2 className="font-display text-2xl">Tarifs</h2>
              </div>
              <div className="space-y-3">
                {stage.tarifs.map((t) => (
                  <div
                    key={t.label}
                    className="flex justify-between items-baseline gap-4 pb-3"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.7)" }}
                  >
                    <span className="text-sm md:text-base" aria-hidden="true">{t.label}</span>
                    <span
                      className="font-display text-2xl whitespace-nowrap"
                      style={{ color: stage.accent }}
                      aria-hidden="true"
                    >
                      {t.value}
                    </span>
                    {/* Même information en une phrase complète, pour les extracteurs de
                        texte (Google, ChatGPT, FastBots) qui lisent des mots, pas une grille. */}
                    <span className="sr-only">{t.label} : {t.value} pour la semaine de {stage.title}.</span>
                  </div>
                ))}
                <div className="text-xs italic text-[var(--muted)] pt-1">
                  Le montant final sera vérifié et confirmé par 1000ARTS lors de la facturation.
                </div>
              </div>
            </div>
            )}

            {/* Parrainage */}
            {stage.parrainage && (
              <div
                className="card-flat flex items-start gap-3"
                style={{ background: "#FFFBEB", borderColor: "#F5D57A" }}
              >
                <Gift
                  size={20}
                  className="mt-0.5 flex-shrink-0"
                  style={{ color: "#B7791F" }}
                />
                <div className="text-sm text-[var(--text)]">
                  <strong className="font-semibold">Parrainage&nbsp;:</strong> {stage.parrainage}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA INSCRIPTION */}
        <section
          className="mt-14 rounded-[32px] p-8 md:p-12 text-center relative overflow-hidden"
          style={{
            background: termine ? "#F3F4F6" : sansDate ? stage.accent_soft : "var(--primary-tint)",
            border: "1px solid var(--border-soft)",
          }}
          data-testid={`stage-cta-section-${stage.id}`}
        >
          {termine ? (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                style={{ background: "#E5E7EB", color: "#6B7280" }}>
                Édition terminée
              </div>
              <h2 className="font-display text-3xl md:text-4xl">
                Les inscriptions à ce stage sont fermées
              </h2>
              <p className="mt-3 text-[var(--muted)] max-w-xl mx-auto">
                Merci à tous les enfants qui ont participé à cette édition. Découvre les prochains stages et sois parmi les premier·es à t'inscrire.
              </p>
              <Link
                to="/stages"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
                style={{ background: "var(--primary)", color: "#fff" }}
                data-testid="stage-back-to-stages"
              >
                Voir tous les stages <ArrowLeft size={16} className="rotate-180" />
              </Link>
            </>
          ) : sansDate ? (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                style={{ background: "#fff", color: stage.accent, boxShadow: "var(--shadow)" }}>
                <Sparkles size={12} /> Date à venir
              </div>
              <h2 className="font-display text-3xl md:text-4xl">
                Programme détaillé à venir
              </h2>
              <p className="mt-3 text-[var(--muted)] max-w-xl mx-auto">
                Les disciplines, tranches d'âge et tarifs seront communiqués prochainement. Contacte-nous pour être averti·e en priorité dès l'ouverture des inscriptions.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <a
                  href={stage.contact.phone_href}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold shadow-md hover:scale-105 transition-transform"
                  style={{ background: stage.accent, color: "#fff" }}
                  data-testid={`stage-cta-phone-${stage.id}`}
                >
                  <Phone size={16} /> {stage.contact.phone}
                </a>
                <a
                  href={`mailto:${stage.contact.email}?subject=${encodeURIComponent(`${stage.title} - Info`)}`}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold shadow-md hover:scale-105 transition-transform"
                  style={{ background: "#fff", color: stage.accent, border: "1px solid var(--border-soft)" }}
                  data-testid={`stage-cta-email-${stage.id}`}
                >
                  Me tenir informé·e
                </a>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                style={{ background: "#fff", color: stage.accent, boxShadow: "var(--shadow)" }}>
                <Sparkles size={12} /> Inscription
              </div>
              <h2 className="font-display text-3xl md:text-4xl">
                Réserve la place de ton enfant
              </h2>
              <p className="mt-3 text-[var(--muted)] max-w-xl mx-auto">
                Remplis le formulaire d'inscription en quelques minutes. Tu recevras une confirmation par e‑mail avec les modalités de paiement.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/inscription?type=stage"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold shadow-md hover:scale-105 transition-transform"
                  style={{ background: "var(--primary)", color: "#fff" }}
                  data-testid={`stage-cta-inscription-${stage.id}`}
                >
                  <Sparkles size={16} /> M'inscrire à ce stage
                </Link>
                <a
                  href={stage.contact.phone_href}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold shadow-md hover:scale-105 transition-transform"
                  style={{ background: "#fff", color: "var(--primary)", border: "1px solid var(--border-soft)" }}
                  data-testid={`stage-cta-phone-${stage.id}`}
                >
                  <Phone size={16} /> {stage.contact.phone}
                </a>
              </div>
              <div className="text-xs text-[var(--muted)] mt-5">
                ou par e‑mail à <a href={`mailto:${stage.contact.email}`} className="underline">{stage.contact.email}</a>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
